import { CalendarIcon } from "@radix-ui/react-icons";
import { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, extractDate, stringToDateRange } from "@/lib/utils";
import { OptionsContext } from "@/provider/options-provider";
import { useContext, useEffect, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { FaLongArrowAltRight } from "react-icons/fa";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export function DateRangePicker<
  TFieldValues extends FieldValues & {
    dateRange?: { from?: Date; to?: Date } | undefined;
  },
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  className,
  form,
  onSubmit,
}: {
  className?: React.ComponentProps<"div">["className"];
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  onSubmit: () => void;
}) {
  const { searchOptions, setSearchOptions } = useContext(OptionsContext);
  const [selectValue, setSelectValue] = useState<string>(
    searchOptions.dateRangeString!,
  );
  const date = searchOptions.dateRange;
  const setDate = (d: DateRange | undefined) => {
    setSearchOptions({
      ...searchOptions,
      dateRange: d,
    });
    setSelectValue("Custom");
  };
  useEffect(() => {
    setSelectValue(searchOptions.dateRangeString!);
  }, [searchOptions.dateRangeString]);

  const savePeriod = async (value: string) => {
    const { ticker: _ticker, ...optionsWithoutTicker } = searchOptions;
    await fetch("/api/user", {
      method: "POST",
      body: JSON.stringify({ ...optionsWithoutTicker, dateRangeString: value }),
    });
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <div className="flex w-full flex-row justify-between">
          <Select
            value={selectValue}
            onValueChange={async (value) => {
              if (value === "Custom") {
                return;
              }

              const newDateRange = stringToDateRange(value);

              setSearchOptions({
                ...searchOptions,
                dateRange: newDateRange,
                dateRangeString: value,
              });

              form.setValue(
                "dateRange" as Path<TFieldValues>,
                newDateRange as PathValue<TFieldValues, Path<TFieldValues>>,
              );
              // if (form.getValues("searchText") || form.getValues("ticker")) {
              //   form.handleSubmit(onSubmit)();
              // }
              setSelectValue(value);
              await savePeriod(value);
            }}
          >
            <SelectTrigger className="rounded-none rounded-l-md">
              <SelectValue placeholder="Select" className="" />
            </SelectTrigger>
            <SelectContent position="popper">
              <SelectItem value="1 Week">1 Week</SelectItem>
              <SelectItem value="2 Weeks">2 Weeks</SelectItem>
              <SelectItem value="1 Month">1 Month</SelectItem>
              <SelectItem value="3 Months">3 Months</SelectItem>
              <SelectItem value="6 Months">6 Months</SelectItem>
              <SelectItem value="1 Year">1 Year</SelectItem>
              <SelectItem value="5 Years">5 Years</SelectItem>
              <SelectItem value="10 Years">10 Years</SelectItem>
              <SelectItem disabled value="Custom">
                <div className="flex flex-row items-center gap-6">
                  <p className="italic text-muted-foreground"> Custom </p>
                  <FaLongArrowAltRight />
                </div>
              </SelectItem>
            </SelectContent>
            <PopoverTrigger
              asChild
              tabIndex={-1}
              className="rounded-none rounded-r-md"
            >
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "ml-1 w-fit justify-start text-left font-normal",
                  !date && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {date?.from && date?.to && (
                  <span className="ml-1 text-xs">
                    {extractDate(date.from)} – {extractDate(date.to)}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
          </Select>
        </div>
        <PopoverContent
          className="flex w-auto flex-col space-y-2 p-2"
          align="start"
        >
          <Calendar
            mode="range"
            captionLayout="dropdown"
            defaultMonth={date?.from}
            selected={date}
            onSelect={setDate}
            numberOfMonths={1}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
